import { useEffect, useRef, useState } from 'react';
import { getLocalizedErrorMessage } from '@/lib/error-i18n';

interface TurnstileOptions {
  sitekey: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  callback?: (token: string) => void;
  errorCallback?: () => void;
  expiredCallback?: () => void;
}

interface UseTurnstileReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  token: string | null;
  reset: () => void;
  isLoading: boolean;
  error: string | null;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string;
    };
    onTurnstileLoad?: () => void;
  }
}

export const useTurnstile = (options: TurnstileOptions): UseTurnstileReturn => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setToken(null);
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const loadTurnstile = () => {
      if (!window.turnstile || !containerRef.current) {
        setError(getLocalizedErrorMessage('Turnstile failed to load', 'errors.securityVerification'));
        setIsLoading(false);
        return;
      }

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: options.sitekey,
          theme: options.theme || 'auto',
          size: options.size || 'normal',
          callback: (turnstileToken: string) => {
            setToken(turnstileToken);
            setIsLoading(false);
            options.callback?.(turnstileToken);
          },
          'error-callback': () => {
            setError(getLocalizedErrorMessage('Turnstile verification failed', 'errors.securityVerification'));
            setIsLoading(false);
            options.errorCallback?.();
          },
          'expired-callback': () => {
            setToken(null);
            setIsLoading(true);
            options.expiredCallback?.();
          },
        });
      } catch (err) {
        setError(getLocalizedErrorMessage(err, 'errors.securityVerification'));
        setIsLoading(false);
      }
    };

    if (window.turnstile) {
      loadTurnstile();
    } else {
      // Wait for Turnstile to load
      window.onTurnstileLoad = loadTurnstile;
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.error('Error removing Turnstile widget:', err);
        }
      }
    };
  }, [options.sitekey, options.theme, options.size]);

  return {
    containerRef,
    token,
    reset,
    isLoading,
    error,
  };
};
